import { motion } from "framer-motion";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { ShieldCheck, Info, UserCheck, Shield, ClipboardList, Plus, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { AppDataTable } from "@/components/ui/app-data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  INITIAL_CATEGORIES,
  INITIAL_EXCEPTIONS,
  INITIAL_METRICS,
  INITIAL_OBSERVATIONS,
  INITIAL_PROFILE,
  INITIAL_WORKFLOW_EVENTS,
  type AppetiteMetricStatus,
  type RiskAppetiteException,
  type RiskAppetiteWorkflowEvent,
  type WorkflowState,
} from "./risk-appetite-static-data";
import { formatMetricValue, getCategoryStatus, getMetricStatus, getProfileStatus } from "./risk-appetite-utils";

const THREE_LINES = [
  {
    line: "1st Line of Defence",
    desc: "Business Operations",
    roles: "Treasury, Finance, Business Unit Leads",
    details: "Own risk-taking activity, day-to-day monitoring, and first response for threshold deviations.",
    icon: UserCheck,
  },
  {
    line: "2nd Line of Defence",
    desc: "Risk & Compliance",
    roles: "CRO Office, Risk Oversight, Compliance",
    details: "Challenge first-line decisions, monitor appetite adherence, and coordinate escalation actions.",
    icon: Shield,
  },
  {
    line: "3rd Line of Defence",
    desc: "Internal Audit",
    roles: "Internal Audit Team",
    details: "Provide independent assurance that appetite governance and controls are operating effectively.",
    icon: ClipboardList,
  },
];

type UpdateFormState = {
  metricId: string;
  actualValue: string;
  comment: string;
  updatedBy: string;
};

const STATUS_STYLE: Record<AppetiteMetricStatus, string> = {
  Within: "border-emerald-500/30 text-emerald-500",
  Pending: "border-slate-500/30 text-slate-400",
  EWI: "border-amber-500/40 text-amber-500",
  "At Limit": "border-orange-500/40 text-orange-500",
  Recovery: "border-red-500/40 text-red-500",
  Capacity: "border-rose-500/50 text-rose-400",
};

const STATUS_SEVERITY: Record<AppetiteMetricStatus, number> = {
  Within: 0,
  Pending: 1,
  EWI: 2,
  "At Limit": 3,
  Recovery: 4,
  Capacity: 5,
};

function getWorkflowBadge(state: WorkflowState) {
  if (state === "approved") return "border-emerald-500/30 text-emerald-500";
  if (state === "under_review") return "border-amber-500/30 text-amber-500";
  if (state === "superseded") return "border-slate-500/30 text-slate-400";
  return "border-blue-500/30 text-blue-500";
}

function formatWorkflowLabel(state: WorkflowState): string {
  if (state === "under_review") return "Under Review";
  if (state === "superseded") return "Superseded";
  if (state === "approved") return "Approved";
  return "Draft";
}

function formatEventAction(action: RiskAppetiteWorkflowEvent["action"]): string {
  if (action === "submit_for_review") return "Submitted for Review";
  if (action === "request_changes") return "Changes Requested";
  if (action === "temporary_breach_accepted") return "Temporary Breach Accepted";
  if (action === "approved") return "Approved";
  return "Created";
}

export default function RiskAppetite() {
  const { toast } = useToast();
  const [profile, setProfile] = useState(INITIAL_PROFILE);
  const [metrics, setMetrics] = useState(INITIAL_METRICS);
  const [observations, setObservations] = useState(INITIAL_OBSERVATIONS);
  const [exceptions, setExceptions] = useState(INITIAL_EXCEPTIONS);
  const [workflowEvents, setWorkflowEvents] = useState(INITIAL_WORKFLOW_EVENTS);
  const [selectedCategoryId, setSelectedCategoryId] = useState(INITIAL_CATEGORIES[0]?.id ?? "");
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [updateForm, setUpdateForm] = useState<UpdateFormState>({
    metricId: INITIAL_METRICS[0]?.id ?? "",
    actualValue: "",
    comment: "",
    updatedBy: "Risk Analyst (Simulation)",
  });

  const profileStatus = useMemo(() => getProfileStatus(metrics), [metrics]);
  const openExceptions = useMemo(() => exceptions.filter((item) => item.status === "Open"), [exceptions]);
  const selectedMetric = useMemo(
    () => metrics.find((item) => item.id === updateForm.metricId) ?? null,
    [metrics, updateForm.metricId],
  );
  const selectedCategory = useMemo(
    () => INITIAL_CATEGORIES.find((category) => category.id === selectedCategoryId) ?? INITIAL_CATEGORIES[0],
    [selectedCategoryId],
  );

  const summaryRows = useMemo(
    () =>
      INITIAL_CATEGORIES.map((category) => ({
        ...category,
        status: getCategoryStatus(metrics, category.id),
        metricsCount: metrics.filter((metric) => metric.categoryId === category.id).length,
      })),
    [metrics],
  );

  const metricRows = useMemo(
    () =>
      metrics
        .filter((metric) => metric.categoryId === selectedCategoryId)
        .sort((a, b) => a.sequence - b.sequence)
        .map((metric) => ({
          ...metric,
          status: getMetricStatus(metric),
          actualLabel: formatMetricValue(metric, metric.actualValue),
        })),
    [metrics, selectedCategoryId],
  );

  useEffect(() => {
    if (!metricRows.length) {
      return;
    }
    const metricBelongsToSelectedCategory = metricRows.some((item) => item.id === updateForm.metricId);
    if (!metricBelongsToSelectedCategory) {
      setUpdateForm((current) => ({
        ...current,
        metricId: metricRows[0].id,
        actualValue: metricRows[0].actualValue === null ? "" : String(metricRows[0].actualValue),
      }));
    }
  }, [metricRows, updateForm.metricId]);

  const appendWorkflowEvent = (
    action: RiskAppetiteWorkflowEvent["action"],
    comment: string,
    actor = "Risk Governance Admin (Simulation)",
  ) => {
    setWorkflowEvents((current) => [
      {
        id: `wf-${Date.now()}`,
        action,
        actor,
        at: new Date().toISOString(),
        comment,
      },
      ...current,
    ]);
  };

  const summaryColumns = useMemo<ColumnDef<(typeof summaryRows)[number]>[]>(
    () => [
      {
        accessorKey: "title",
        header: "Risk Category",
        cell: ({ row }) => <span className="font-semibold">{row.original.title}</span>,
        meta: { headerClassName: "w-[210px]" },
      },
      {
        accessorKey: "statement",
        header: "Qualitative Appetite Statement",
        cell: ({ row }) => <p className="text-sm leading-relaxed text-muted-foreground">{row.original.statement}</p>,
      },
      {
        accessorKey: "tolerance",
        header: "Tolerance",
        cell: ({ row }) => (
          <Badge variant="outline" className="font-normal border-primary/30 text-primary">
            {row.original.tolerance}
          </Badge>
        ),
        meta: { headerClassName: "w-[120px]" },
      },
      {
        accessorKey: "status",
        header: "Current Status",
        cell: ({ row }) => (
          <Badge variant="outline" className={STATUS_STYLE[row.original.status]}>
            {row.original.status}
          </Badge>
        ),
        meta: { headerClassName: "w-[140px]" },
      },
      {
        accessorKey: "metricsCount",
        header: "Metrics",
        cell: ({ row }) => <span className="text-sm">{row.original.metricsCount}</span>,
        meta: { headerClassName: "w-[80px]" },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedCategoryId(row.original.id);
            }}
          >
            View Metrics
          </Button>
        ),
        meta: { headerClassName: "w-[120px] text-right", cellClassName: "text-right" },
      },
    ],
    [],
  );

  const metricColumns = useMemo<ColumnDef<(typeof metricRows)[number]>[]>(
    () => [
      {
        accessorKey: "sequence",
        header: "#",
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.sequence}</span>,
        meta: { headerClassName: "w-[48px]" },
      },
      {
        accessorKey: "metric",
        header: "Metric",
        cell: ({ row }) => <span className="font-semibold">{row.original.metric}</span>,
      },
      {
        id: "level1",
        header: "Level 1 Trigger / EWI",
        cell: ({ row }) => row.original.thresholds.level1.label,
        meta: { headerClassName: "w-[160px]" },
      },
      {
        id: "level2",
        header: "Level 2 Risk Appetite Limit",
        cell: ({ row }) => row.original.thresholds.level2.label,
        meta: { headerClassName: "w-[150px]" },
      },
      {
        id: "level3",
        header: "Level 3 Recovery",
        cell: ({ row }) => row.original.thresholds.level3.label,
        meta: { headerClassName: "w-[130px]" },
      },
      {
        id: "level4",
        header: "Level 4 Capacity",
        cell: ({ row }) => row.original.thresholds.level4.label,
        meta: { headerClassName: "w-[130px]" },
      },
      {
        id: "actual",
        header: "Actual",
        cell: ({ row }) => <span className="font-medium">{row.original.actualLabel}</span>,
        meta: { headerClassName: "w-[140px]" },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge variant="outline" className={STATUS_STYLE[row.original.status]}>
            {row.original.status}
          </Badge>
        ),
        meta: { headerClassName: "w-[110px]" },
      },
      {
        accessorKey: "trend",
        header: "Trend",
        cell: ({ row }) => {
          if (row.original.trend === "up") {
            return (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-500">
                <ArrowUpRight className="h-3.5 w-3.5" />
                Up
              </span>
            );
          }
          if (row.original.trend === "down") {
            return (
              <span className="inline-flex items-center gap-1 text-xs text-red-500">
                <ArrowDownRight className="h-3.5 w-3.5" />
                Down
              </span>
            );
          }
          return (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Minus className="h-3.5 w-3.5" />
              Flat
            </span>
          );
        },
        meta: { headerClassName: "w-[95px]" },
      },
      {
        accessorKey: "lastUpdated",
        header: "Last Updated",
        meta: { headerClassName: "w-[120px]" },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setUpdateForm((current) => ({
                ...current,
                metricId: row.original.id,
                actualValue: row.original.actualValue === null ? "" : String(row.original.actualValue),
              }));
              setIsUpdateDialogOpen(true);
            }}
          >
            Update
          </Button>
        ),
        meta: { headerClassName: "w-[100px] text-right", cellClassName: "text-right" },
      },
    ],
    [],
  );

  const workflowColumns = useMemo<ColumnDef<RiskAppetiteWorkflowEvent>[]>(
    () => [
      {
        accessorKey: "action",
        header: "Action",
        cell: ({ row }) => <span className="font-medium">{formatEventAction(row.original.action)}</span>,
        meta: { headerClassName: "w-[190px]" },
      },
      {
        accessorKey: "actor",
        header: "Actor",
        meta: { headerClassName: "w-[220px]" },
      },
      {
        accessorKey: "at",
        header: "At",
        cell: ({ row }) => new Date(row.original.at).toLocaleString(),
        meta: { headerClassName: "w-[170px]" },
      },
      {
        accessorKey: "comment",
        header: "Comment",
      },
    ],
    [],
  );

  const exceptionColumns = useMemo<ColumnDef<RiskAppetiteException>[]>(
    () => [
      {
        id: "metric",
        header: "Metric",
        cell: ({ row }) => metrics.find((metric) => metric.id === row.original.metricId)?.metric ?? row.original.metricId,
      },
      {
        accessorKey: "reason",
        header: "Reason",
      },
      {
        accessorKey: "approvedBy",
        header: "Approved By",
        meta: { headerClassName: "w-[190px]" },
      },
      {
        accessorKey: "expiresAt",
        header: "Expires",
        meta: { headerClassName: "w-[120px]" },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge variant="outline" className={row.original.status === "Open" ? "border-amber-500/30 text-amber-500" : "border-emerald-500/30 text-emerald-500"}>
            {row.original.status}
          </Badge>
        ),
        meta: { headerClassName: "w-[100px]" },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline"
            disabled={row.original.status !== "Open"}
            onClick={() => {
              setExceptions((current) =>
                current.map((item) => (item.id === row.original.id ? { ...item, status: "Closed" } : item)),
              );
              appendWorkflowEvent(
                "temporary_breach_accepted",
                `Temporary breach accepted for ${metrics.find((metric) => metric.id === row.original.metricId)?.metric ?? "selected metric"}.`,
                "Chief Risk Officer (Simulation)",
              );
              toast({
                title: "Temporary breach accepted",
                description: "Exception status moved to closed for simulation flow.",
              });
            }}
          >
            Accept
          </Button>
        ),
        meta: { headerClassName: "w-[100px] text-right", cellClassName: "text-right" },
      },
    ],
    [metrics, toast],
  );

  const submitWorkflowAction = (action: "submit_for_review" | "approved" | "request_changes") => {
    if (action === "submit_for_review") {
      setProfile((current) => ({ ...current, workflowState: "under_review" }));
      appendWorkflowEvent("submit_for_review", "Draft submitted for second-line review.");
      toast({
        title: "Submitted for review",
        description: "Governance state moved to Under Review (simulation).",
      });
      return;
    }

    if (action === "approved") {
      setProfile((current) => ({ ...current, workflowState: "approved" }));
      appendWorkflowEvent("approved", "Risk Appetite dashboard approved for committee reporting.");
      toast({
        title: "Approved",
        description: "Governance state moved to Approved (simulation).",
      });
      return;
    }

    setProfile((current) => ({ ...current, workflowState: "draft" }));
    appendWorkflowEvent("request_changes", "Changes requested before approval.");
    toast({
      title: "Changes requested",
      description: "Governance state moved back to Draft (simulation).",
    });
  };

  const recordMetricUpdate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const metric = metrics.find((item) => item.id === updateForm.metricId);
    if (!metric) {
      return;
    }
    const parsedValue = Number(updateForm.actualValue);
    if (Number.isNaN(parsedValue)) {
      toast({
        title: "Invalid value",
        description: "Provide a valid numeric value for the selected metric.",
        variant: "destructive",
      });
      return;
    }

    const previousStatus = getMetricStatus(metric);
    const previousValue = metric.actualValue;
    const nextTrend = previousValue === null ? "flat" : parsedValue > previousValue ? "up" : parsedValue < previousValue ? "down" : "flat";
    const updatedMetric = {
      ...metric,
      actualValue: parsedValue,
      trend: nextTrend,
      lastUpdated: new Date().toISOString().slice(0, 10),
    };
    const newStatus = getMetricStatus(updatedMetric);
    const statusWorsened = STATUS_SEVERITY[newStatus] > STATUS_SEVERITY[previousStatus];
    if (statusWorsened && !updateForm.comment.trim()) {
      toast({
        title: "Comment required",
        description: "Add a comment when status worsens or escalation is triggered.",
        variant: "destructive",
      });
      return;
    }

    setMetrics((current) => current.map((item) => (item.id === metric.id ? updatedMetric : item)));
    setObservations((current) => [
      {
        id: `obs-${Date.now()}`,
        metricId: metric.id,
        previousValue,
        newValue: parsedValue,
        comment: updateForm.comment.trim() || "No additional comment provided.",
        updatedBy: updateForm.updatedBy,
        updatedAt: new Date().toISOString(),
      },
      ...current,
    ]);

    if (["At Limit", "Recovery", "Capacity"].includes(newStatus)) {
      setExceptions((current) => [
        {
          id: `exc-${Date.now()}`,
          metricId: metric.id,
          reason:
            updateForm.comment.trim() ||
            `Auto-created due to ${newStatus} status after manual metric update (simulation).`,
          approvedBy: "Pending committee approval",
          expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 14).toISOString().slice(0, 10),
          createdAt: new Date().toISOString().slice(0, 10),
          status: "Open",
        },
        ...current,
      ]);
    }

    toast({
      title: "Metric update recorded",
      description: `${metric.metric} is now ${newStatus} (${previousStatus} before update).`,
    });
    setIsUpdateDialogOpen(false);
    setUpdateForm((current) => ({
      ...current,
      actualValue: "",
      comment: "",
    }));
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-medium text-foreground">Risk Appetite</h1>
          <p className="text-muted-foreground">
            RMF-aligned risk appetite summary and metrics dashboard (frontend simulation only).
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="border-primary/30 text-primary">
              Simulation Mode - Frontend Only
            </Badge>
            <Badge variant="outline" className={STATUS_STYLE[profileStatus]}>
              Profile Status: {profileStatus}
            </Badge>
            <Badge variant="outline" className={getWorkflowBadge(profile.workflowState)}>
              Workflow: {formatWorkflowLabel(profile.workflowState)}
            </Badge>
          </div>
        </div>
        <Button
          className="w-fit"
          disabled={!metricRows.length}
          onClick={() => {
            setUpdateForm((current) => ({
              ...current,
              metricId: metricRows[0]?.id ?? current.metricId,
              actualValue: "",
              comment: "",
            }));
            setIsUpdateDialogOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Record Category Metric Update
        </Button>
      </div>

      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="governance">Governance</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-6">
          <Card className="border-none shadow-premium overflow-hidden">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                Risk Appetite Summary
              </CardTitle>
              <CardDescription>
                Qualitative appetite statements aligned to principal risk categories.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <AppDataTable
                columns={summaryColumns}
                data={summaryRows}
                getRowId={(row) => row.id}
                onRowClick={(row) => setSelectedCategoryId(row.id)}
                rowClassName={(row) =>
                  row.id === selectedCategoryId ? "bg-primary/5 border-l-2 border-primary" : "hover:bg-primary/5"
                }
                emptyMessage="No appetite categories available."
              />
            </CardContent>
          </Card>

          <Card className="border-none shadow-premium overflow-hidden">
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div className="space-y-1">
                <CardTitle>{selectedCategory?.title ?? "Selected Category"} Metrics Dashboard</CardTitle>
                <CardDescription>
                  Category-specific threshold dashboard. Click a risk category row above (or View Metrics) to switch context.
                </CardDescription>
              </div>
              <Badge variant="outline" className="border-primary/30 text-primary">
                Simulation Mode
              </Badge>
            </CardHeader>
            <CardContent className="pt-0">
              <AppDataTable
                columns={metricColumns}
                data={metricRows}
                getRowId={(row) => row.id}
                emptyMessage="No metrics configured for this risk category."
              />
            </CardContent>
          </Card>

          <Card className="border-none shadow-premium overflow-hidden">
            <CardHeader>
              <CardTitle>Recent Manual Updates ({selectedCategory?.title ?? "Selected Category"})</CardTitle>
              <CardDescription>
                Session-level observation history for selected category metrics (not persisted to backend in this phase).
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                {observations
                  .filter((entry) => metrics.find((item) => item.id === entry.metricId)?.categoryId === selectedCategoryId)
                  .slice(0, 6)
                  .map((entry) => {
                    const metricName = metrics.find((item) => item.id === entry.metricId)?.metric ?? entry.metricId;
                    return (
                      <div key={entry.id} className="rounded-lg border bg-muted/20 p-3">
                        <p className="text-sm font-medium">{metricName}</p>
                        <p className="text-xs text-muted-foreground">
                          {entry.updatedBy} - {new Date(entry.updatedAt).toLocaleString()}
                        </p>
                        <p className="text-xs mt-1 text-muted-foreground">
                          {entry.previousValue === null ? "TBD" : entry.previousValue} {"->"}{" "}
                          {entry.newValue === null ? "TBD" : entry.newValue}
                        </p>
                        <p className="text-xs mt-1">{entry.comment}</p>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>

          <section className="space-y-6">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Three Lines of Defence
              </h2>
              <p className="text-sm text-muted-foreground italic">
                RMF accountability model represented as simulation metadata in this static phase.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {THREE_LINES.map((item, index) => (
                <motion.div
                  key={item.line}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full border-none shadow-premium bg-gradient-to-b from-card to-background">
                    <CardHeader className="text-center">
                      <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                        <item.icon className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="text-primary text-lg">{item.line}</CardTitle>
                      <CardDescription className="font-bold">{item.desc}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 text-center">
                      <p className="text-sm text-muted-foreground">{item.details}</p>
                      <div className="pt-4 border-t border-muted">
                        <span className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground block mb-2">
                          Roles
                        </span>
                        <p className="text-xs font-medium">{item.roles}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="governance" className="space-y-4">
          <Card className="border-none shadow-premium">
            <CardHeader>
              <CardTitle>Governance Workflow (Simulated)</CardTitle>
              <CardDescription>
                Approval lifecycle and annual review metadata are UI-only in this phase.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-lg border p-3">
                  <p className="text-xs uppercase text-muted-foreground">Effective From</p>
                  <p className="font-semibold">{profile.effectiveFrom}</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs uppercase text-muted-foreground">Annual Review Due</p>
                  <p className="font-semibold">{profile.nextAnnualReviewDate}</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs uppercase text-muted-foreground">Current Workflow</p>
                  <Badge variant="outline" className={getWorkflowBadge(profile.workflowState)}>
                    {formatWorkflowLabel(profile.workflowState)}
                  </Badge>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={profile.workflowState !== "draft"}
                  onClick={() => submitWorkflowAction("submit_for_review")}
                >
                  Submit for Review
                </Button>
                <Button
                  variant="outline"
                  disabled={profile.workflowState !== "under_review"}
                  onClick={() => submitWorkflowAction("request_changes")}
                >
                  Request Changes
                </Button>
                <Button
                  disabled={profile.workflowState !== "under_review"}
                  onClick={() => submitWorkflowAction("approved")}
                >
                  Approve
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-premium overflow-hidden">
            <CardHeader>
              <CardTitle>Temporary Breach Exceptions</CardTitle>
              <CardDescription>
                Open exceptions are generated when metrics move to At Limit / Recovery / Capacity.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <AppDataTable
                columns={exceptionColumns}
                data={openExceptions}
                getRowId={(row) => row.id}
                emptyMessage="No open temporary breach exceptions."
              />
            </CardContent>
          </Card>

          <Card className="border-none shadow-premium overflow-hidden">
            <CardHeader>
              <CardTitle>Workflow Event Timeline</CardTitle>
              <CardDescription>Audit-style log for governance actions in simulation mode.</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <AppDataTable
                columns={workflowColumns}
                data={workflowEvents}
                getRowId={(row) => row.id}
                emptyMessage="No workflow events yet."
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card className="bg-primary/5 border-none shadow-sm flex items-start gap-4 p-6">
        <Info className="h-6 w-6 text-primary shrink-0" />
        <div className="space-y-1">
          <h4 className="font-bold text-sm">Simulation Mode Note</h4>
          <p className="text-xs leading-relaxed text-muted-foreground">
            This view demonstrates RMF-aligned appetite governance using frontend-only static data. Workflow,
            exceptions, and history are intentionally simulated and will be backed by Supabase in a future phase.
          </p>
        </div>
      </Card>

      <Dialog open={isUpdateDialogOpen} onOpenChange={setIsUpdateDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Record Metric Update</DialogTitle>
            <DialogDescription>
              Manual update simulation. Data is stored in local UI state only for this session.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={recordMetricUpdate}>
            <div className="space-y-2">
              <Label>Metric</Label>
              <Select
                value={updateForm.metricId}
                onValueChange={(value) =>
                  setUpdateForm((current) => ({
                    ...current,
                    metricId: value,
                    actualValue: "",
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select metric" />
                </SelectTrigger>
                <SelectContent>
                  {metricRows.map((metric) => (
                    <SelectItem key={metric.id} value={metric.id}>
                      {metric.sequence}. {metric.metric}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="actual-value">Actual Value</Label>
                <Input
                  id="actual-value"
                  type="number"
                  step="0.01"
                  value={updateForm.actualValue}
                  onChange={(event) => setUpdateForm((current) => ({ ...current, actualValue: event.target.value }))}
                  placeholder="Enter numeric value"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="updated-by">Updated By</Label>
                <Input
                  id="updated-by"
                  value={updateForm.updatedBy}
                  onChange={(event) => setUpdateForm((current) => ({ ...current, updatedBy: event.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="update-comment">Comment</Label>
              <Textarea
                id="update-comment"
                value={updateForm.comment}
                onChange={(event) => setUpdateForm((current) => ({ ...current, comment: event.target.value }))}
                placeholder="Required when status worsens."
                className="min-h-[90px]"
              />
              {selectedMetric ? (
                <p className="text-xs text-muted-foreground">
                  Current value: {formatMetricValue(selectedMetric, selectedMetric.actualValue)} / Current status:{" "}
                  {getMetricStatus(selectedMetric)}
                </p>
              ) : null}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsUpdateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Update</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
